import type { MonitorChannelMessage } from './types';
import { MONITOR_CHANNEL_NAME } from './types';

type MessageHandler = (message: MonitorChannelMessage) => void;

/**
 * Thin wrapper around BroadcastChannel for cross-window communication.
 *
 * All multi-monitor windows (primary + child viewers) share the same channel
 * name so messages are automatically delivered to every same-origin window.
 */
export class MonitorChannel {
  private channel: BroadcastChannel | null = null;
  private handlers = new Set<MessageHandler>();

  /** Open the channel. Safe to call multiple times (idempotent). */
  open(): void {
    if (this.channel) return;
    this.channel = new BroadcastChannel(MONITOR_CHANNEL_NAME);
    this.channel.onmessage = (event: MessageEvent<MonitorChannelMessage>) => {
      for (const handler of this.handlers) {
        try {
          handler(event.data);
        } catch {
          // Don't let one bad handler kill the channel
        }
      }
    };
  }

  /** Close the channel and remove all handlers. */
  close(): void {
    this.channel?.close();
    this.channel = null;
    this.handlers.clear();
  }

  /** Send a message to all other windows on this channel. */
  send(message: MonitorChannelMessage): void {
    this.channel?.postMessage(message);
  }

  /** Subscribe to incoming messages. Returns an unsubscribe function. */
  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** Whether the underlying BroadcastChannel is open. */
  get isOpen(): boolean {
    return this.channel !== null;
  }
}
